import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ChartArea, ExternalLink } from 'lucide-react';

import styled from 'styled-components';

import { Loader, LoaderModeType } from 'components/Loader';
import {
    EntityPageBreadcrumb,
    PageActions,
    PageActionsMenu,
    PageHeader,
    type PageActionGroup,
} from 'components/PageHeader';
import { DetailValue } from 'components/DetailsView';

import { getExtendedFormattedDatetime } from 'helpers/dateTimes';
import { FutureStockVariationsList } from './components/FutureStockVariationsList';
import { StockVariationsList } from './components/StockVariationsList';
import { SiteType, useGetColumnProductQuery, useGetColumnQuery, TotadminPermission } from 'data/__generated__';
import { AvailableStocksDetailsPopup } from './AvailableStocksDetailsPopup';

type ParamTypes = {
    columnId: string;
    productId: string;
};

export const ColumnProductStocks = () => {
    const { columnId = '', productId = '' } = useParams<ParamTypes>();

    const [isAvailableStockDetailsPopupOpen, setIsAvailableStockDetailsPopupOpen] = useState<boolean>(false);

    const {
        loading: columnLoading,
        data: columnData,
        error: columnError,
    } = useGetColumnQuery({
        variables: {
            _id: columnId,
        },
    });

    const {
        loading: productLoading,
        data: productData,
        error: productError,
    } = useGetColumnProductQuery({
        variables: {
            columnId,
            productId,
        },
        skip: !columnId,
    });

    const loadError = columnError || productError;
    useEffect(() => {
        if (loadError) {
            toast.error("Une erreur est survenue lors de la récupération de l'historique de stock du produit");
        }
    }, [loadError]);

    if (columnLoading || productLoading) {
        return (
            <Container>
                <Loader mode={LoaderModeType.Spin} />
            </Container>
        );
    }

    if (columnError || !columnData?.column || productError || !productData) {
        return null;
    }

    const column = columnData.column;
    const { product, stock, availableStock, variations, inventories, lastColumnInventoryStock } =
        productData.columnProduct;
    const site = column.site;
    const totem = site.totem;
    const organization = totem.organization;
    const columnGroup = column.microstoreColumn?.columnGroup;

    const actionGroups: PageActionGroup[] = [
        {
            items: [
                {
                    label: 'Détails des variations de stock',
                    icon: <ChartArea />,
                    onClick: () => setIsAvailableStockDetailsPopupOpen(true),
                    dataTest: 'column-product-stocks-available-details',
                },
            ],
        },
        {
            label: 'Navigation',
            items: [
                {
                    label: 'Voir le produit',
                    icon: <ExternalLink />,
                    to: `/product/${product._id}`,
                    dataTest: 'column-product-stocks-view-product',
                    requiredPermissions: [TotadminPermission.ProductsRead],
                },
            ],
        },
    ];

    return (
        <Container>
            <PageHeader>
                <EntityPageBreadcrumb
                    organization={organization}
                    totem={totem}
                    site={site}
                    columnGroup={columnGroup}
                    column={column}
                    product={{ _id: product._id, name: product.fullname }}
                    stocksView
                />
                <PageActions>
                    <PageActionsMenu groups={actionGroups} />
                </PageActions>
            </PageHeader>
            <Content>
                <InfoContainer>
                    <DetailValue label="Colonne" value={column.name} />
                    <DetailValue label="Site" value={site.name} />
                    <DetailValue label="Produit" value={product.fullname} />
                    <DetailValue label="Stock" value={stock} />
                    <DetailValue label="Stock disponible (pas réservé) aujourd'hui" value={availableStock.value} />
                </InfoContainer>

                <InfoContainer>
                    <DetailValue label="Dernier inventaire" value={lastColumnInventoryStock._id} />
                    <DetailValue label="Créé par" value={lastColumnInventoryStock.createdByName || 'Inconnu'} />
                    <DetailValue
                        label="Date"
                        value={getExtendedFormattedDatetime(lastColumnInventoryStock.createdAt)}
                    />
                    <DetailValue label="Stock" value={lastColumnInventoryStock.value} />
                </InfoContainer>

                <Section>
                    <SectionTitle> Variations à venir </SectionTitle>
                    {site.type === SiteType.Warehouse ? (
                        <SectionContent>
                            <FutureStockVariationsList productId={productId} warehouseColumnId={columnId} />
                        </SectionContent>
                    ) : (
                        <SectionCenterContent>
                            Bientôt vous retrouverez les futures stocks variations de chaque site ici
                        </SectionCenterContent>
                    )}
                </Section>
                <Section>
                    <SectionTitle> Variations passées </SectionTitle>
                    <SectionContent>
                        <StockVariationsList stockVariations={variations} inventories={inventories} />
                    </SectionContent>
                </Section>
            </Content>

            <AvailableStocksDetailsPopup
                details={availableStock.details}
                isOpen={isAvailableStockDetailsPopupOpen}
                setIsOpen={setIsAvailableStockDetailsPopupOpen}
            />
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const Content = styled.div`
    padding: 0 15px 15px;
    flex: 1;
    overflow: auto;
`;

const InfoContainer = styled.div`
    display: flex;
    background-color: ${({ theme }) => theme.cardBackgroundColor};
    border-radius: ${({ theme }) => theme.borderRadius};
    border: 1px solid ${({ theme }) => theme.lightBorderColor};
    margin-top: 15px;
    padding: 10px;

    & > :not(:first-child) {
        margin-left: 20px;
    }
`;

const Section = styled.div`
    border-radius: ${({ theme }) => theme.borderRadius};
    border: 1px solid ${({ theme }) => theme.lightBorderColor};
    margin-top: 15px;
    padding: 20px;
`;

const SectionContent = styled.div`
    display: flex;
`;

const SectionCenterContent = styled.div`
    text-align: center;
`;

const SectionTitle = styled.h2`
    text-align: center;
`;
